import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';

/**
 * Sprint C — WhatChangedCard
 * 2–3 compact bullets from /api/home/briefing → `what_changed`.
 * Each item: { label, tone: 'positive' | 'neutral' | 'watchful', link?: string }
 *
 * Stage 20B+ Wave C — items may now carry an optional `link` field. When
 * present, the bullet becomes a tappable deep-link into the relevant surface
 * (/appointments, /documents, /health) so the "what changed" observation
 * instantly becomes actionable. Non-linked bullets render as passive text.
 */
const TONE_DOT = {
  positive: 'bg-emerald-400',
  neutral: 'bg-slate-300',
  watchful: 'bg-amber-400',
};

export const WhatChangedCard = ({ items, loading }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <section data-testid="what-changed-loading" className="animate-pulse">
        <div className="h-3 w-32 bg-[#D9D2C4]/70 rounded mb-3" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 bg-[#ECE8E1]/60 rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <section data-testid="what-changed" aria-labelledby="what-changed-heading">
      <h3
        id="what-changed-heading"
        className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 mb-3 px-1"
      >
        What changed
      </h3>
      <ul className="space-y-2">
        {items.slice(0, 3).map((item, i) => {
          const dot = TONE_DOT[item.tone] || TONE_DOT.neutral;
          const linkable = !!item?.link;
          const row = (
            <>
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} aria-hidden />
              <span className="text-[13.5px] text-[#0E1C2E] leading-relaxed flex-1">
                {item.label}
              </span>
              {linkable && (
                <CaretRight
                  size={14}
                  weight="bold"
                  className="flex-shrink-0 mt-1 text-slate-400 group-hover:text-[#0E1C2E] transition-colors"
                  aria-hidden
                />
              )}
            </>
          );
          if (linkable) {
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => navigate(item.link)}
                  className="group w-full flex items-start gap-3 bg-white border border-[#E5DFD0] rounded-xl px-4 py-3 text-left hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors"
                  data-testid={`what-changed-item-${i}`}
                >
                  {row}
                </button>
              </li>
            );
          }
          return (
            <li
              key={i}
              data-testid={`what-changed-item-${i}`}
              className="flex items-start gap-3 bg-white border border-[#E5DFD0] rounded-xl px-4 py-3"
            >
              {row}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default WhatChangedCard;
